// utils/formDataUtils.js

import { PREDEFINED_SEMINARS, SERVICE_TYPES } from "../step2.constants";

/**
 * Merges two arrays of objects based on a common property (like id)
 */
export const flattenServicesAndSeminars = (servicesData, spaceData) => {
  const mergeByProperty = (arr1, arr2, prop) => {
    const result = [...arr1];
    
    arr2.forEach((arr2obj) => {
      const existingIndex = result.findIndex((arr1obj) => arr1obj[prop] === arr2obj[prop]);
      if (existingIndex !== -1) {
        // CRITICAL: When merging, preserve ALL fields from spaceData
        result[existingIndex] = { 
          ...result[existingIndex], 
          ...arr2obj,
          // CRITICAL: Preserve is_custom flag - priority to arr2obj (spaceData)
          is_custom: arr2obj.is_custom !== undefined ? arr2obj.is_custom : result[existingIndex].is_custom,
          // Ensure price from spaceData is not overwritten by null from placeServiceData
          price: arr2obj.price !== undefined && arr2obj.price !== null 
            ? arr2obj.price 
            : result[existingIndex].price,
          indicative_price: arr2obj.indicative_price !== undefined && arr2obj.indicative_price !== null
            ? arr2obj.indicative_price
            : result[existingIndex].indicative_price
        };
      } else {
        result.push(arr2obj);
      }
    });
    
    return result;
  };

  /**
   * STEP 1: Transform raw form services data
   */
  let services = Object.keys(servicesData)
    .filter((key) => key !== "seminar")
    .flatMap((key) => {
      switch (key) {
        case "onsite":
          // Convert onsite object to array and process each service
          const onsiteServices = Object.values(servicesData[key]);
          console.log('📦 Processing onsite services:', onsiteServices.length);
          
          return onsiteServices.map((service) => {
            // CRITICAL: Log each service to debug
            console.log(`  Processing service: ${service.name}, is_custom: ${service.is_custom}, id: ${service.id}`);
            
            return {
              ...service,
              place_id: service.place_id || null,
              available: false,
              type: SERVICE_TYPES.ONSITE,
              // CRITICAL: Explicitly preserve is_custom flag
              is_custom: service.is_custom === 1 ? 1 : 0,
              // Initialize price fields
              price: service.price !== undefined && service.price !== null ? service.price : null,
              indicative_price: null,
              location_type: null,
              description: null
            };
          });
          
        case "reservation":
          return Object.values(servicesData[key]).map((service) => ({
            ...service,
            place_id: null,
            location_type: "hour",
            description: null,
            price: null,
            available: false,
            type: SERVICE_TYPES.RESERVATION,
            is_custom: 0
          }));
          
        case "free":
          return servicesData[key].map((service) => ({
            ...service,
            place_id: null,
            available: false,
            type: SERVICE_TYPES.FREE,
            is_custom: 0
          }));
          
        default:
          return servicesData[key].map((service) => ({
            ...service,
            place_id: null,
            available: false,
            is_custom: 0
          }));
      }
    });

  console.log('📊 After STEP 1 - Total services:', services.length);
  console.log('📊 Custom services after STEP 1:', services.filter(s => s.is_custom === 1).length);

  /**
   * STEP 2: Merge with existing space data - PRESERVE ALL FLAGS AND TYPES
   */
  if (spaceData?.services && Array.isArray(spaceData.services)) {
    console.log('📦 Merging with spaceData.services:', spaceData.services.length);
    
    const formattedServices = spaceData.services.map((service) => {
      // Start with a copy of the service
      const serv = { ...service };
      
      // Deep merge place_service
      if (service.place_service && typeof service.place_service === 'object') {
        Object.keys(service.place_service).forEach(key => {
          // Skip price-related fields - keep them from the parent service
          if (key !== 'price' && key !== 'indicative_price' && service.place_service[key] !== undefined) {
            serv[key] = service.place_service[key];
          }
        });
      }
      
      // CRITICAL: Preserve is_custom flag - check multiple sources
      serv.is_custom = service.is_custom || 
                      service.place_service?.is_custom || 
                      (service.type === 'onsite' && service.name === 'newCustomService' ? 1 : 0);
      
      // CRITICAL: Ensure price is properly set from the service object
      if (service.price !== undefined && service.price !== null) {
        serv.price = service.price;
      }
      
      if (service.indicative_price !== undefined && service.indicative_price !== null) {
        serv.indicative_price = service.indicative_price;
      }
      
      // Ensure available flag is preserved
      if (service.available !== undefined) {
        serv.available = service.available;
      }
      
      // CRITICAL: Preserve type
      if (!serv.type) {
        if (service.place_service?.type) {
          serv.type = service.place_service.type;
        } else if (serv.location_type === 'included' || serv.location_type === 'hour') {
          serv.type = SERVICE_TYPES.RESERVATION;
        } else if (serv.indicative_price !== undefined) {
          serv.type = SERVICE_TYPES.ONSITE;
        } else {
          serv.type = SERVICE_TYPES.FREE;
        }
      }
      
      delete serv.place_service;
      return serv;
    });
    
    services = mergeByProperty(services, formattedServices, "id");
    console.log('📊 After STEP 2 - Total services:', services.length);
    console.log('📊 Custom services after STEP 2:', services.filter(s => s.is_custom === 1).length);
  }

  /**
   * STEP 3: Sanitize any problematic data and ensure custom services are marked
   */
  services = services.map(service => {
    const cleanService = { ...service };
    
    // Sanitize name
    if (cleanService.name && typeof cleanService.name === 'string') {
      cleanService.name = cleanService.name
        .replace(/\0/g, '')
        .replace(/[^\x20-\x7E\u00A0-\uFFFF]/g, '')
        .trim();
    }
    
    // CRITICAL: Double-check and fix is_custom for known custom services
    // If service name is "newCustomService" or it has a place_id (existing custom service)
    if (cleanService.name === 'newCustomService' || 
        (cleanService.place_id && cleanService.type === 'onsite')) {
      cleanService.is_custom = 1;
      console.log(`✅ Fixed is_custom for service: ${cleanService.name} (id: ${cleanService.id})`);
    }
    
    // Ensure type is never null or undefined
    if (!cleanService.type) {
      console.warn('Service missing type, assigning default:', cleanService.id, cleanService.name);
      cleanService.type = SERVICE_TYPES.FREE;
    }
    
    return cleanService;
  });

  // FINAL DEBUG: Log all custom services found
  const customServicesFound = services.filter(s => s.is_custom === 1);
  console.log('🎯 FINAL - Custom services found:', customServicesFound.length);
  customServicesFound.forEach(s => {
    console.log(`  ✅ ${s.name} (id: ${s.id}, is_custom: ${s.is_custom}, place_id: ${s.place_id})`);
  });

  /**
   * STEP 4: Handle seminars
   */
  let seminars = servicesData.seminar && servicesData.seminar.length > 0
    ? servicesData.seminar.map((sem) => ({
        ...sem,
        price: null,
        offer: null,
      }))
    : [...PREDEFINED_SEMINARS];

  if (spaceData?.seminars) {
    const formattedSeminars = spaceData.seminars.map((seminar) => ({
      ...seminar,
      price: parseFloat(seminar.price),
    }));
    seminars = mergeByProperty(seminars, formattedSeminars, "id");
  }

  return { services, seminars };
};

/**
 * Prepares form data for API submission
 */
export const prepareSubmissionData = (formValues, imageQueueRef) => {
  const safeFormValues = formValues || {};
  const safeImageQueueRef = imageQueueRef || { current: new Map() };
  
  // ============ SERVICE TRANSFORMATIONS ============
  
  /**
   * Transform custom services (new ones being added)
   */
  const validCustomServices = (safeFormValues.custom_services || [])
    .filter(service => service && service.name && service.name.trim() !== "" && service.price && service.price !== "")
    .map(service => ({
      name: service.name.trim(),
      price: parseFloat(service.price),
      available: true,
      type: SERVICE_TYPES.ONSITE,
      is_custom: 1
    }));

  /**
   * Transform existing/predefined services - PRESERVE TYPES AND CUSTOM FLAG
   */
  const transformedServices = (safeFormValues.services || []).map(service => {
    // Handle price conversion
    let price = null;
    if (service.price !== undefined && service.price !== null && service.price !== '') {
      price = parseFloat(service.price);
      if (isNaN(price)) {
        price = null;
      }
    }
    
    // Handle indicative price
    let indicative_price = null;
    if (service.indicative_price !== undefined && service.indicative_price !== null && service.indicative_price !== '') {
      indicative_price = parseFloat(service.indicative_price);
      if (isNaN(indicative_price)) {
        indicative_price = null;
      }
    }
    
    const transformed = {
      ...service,
      price: price,
      indicative_price: indicative_price
    };
    
    // If location_type is 'included', set price to 0
    if (service.location_type === 'included') {
      transformed.price = 0;
    }
    
    // Ensure type is preserved
    if (!transformed.type) {
      console.warn('Service missing type in prepareSubmissionData:', service.id);
      if (transformed.location_type === 'included' || transformed.location_type === 'hour') {
        transformed.type = SERVICE_TYPES.RESERVATION;
      } else if (transformed.indicative_price !== undefined && transformed.indicative_price !== null) {
        transformed.type = SERVICE_TYPES.ONSITE;
      } else {
        transformed.type = SERVICE_TYPES.FREE;
      }  
    }
    
    // CRITICAL: Preserve is_custom flag for existing custom services
    if (service.is_custom === 1 || service.name === 'newCustomService') {
      transformed.is_custom = 1;
    }
    
    return transformed;
  });

  /**
   * Transform seminars
   */
  const transformedSeminars = (safeFormValues.seminars || []).map(seminar => ({
    ...seminar,
    price: seminar.price ? parseFloat(seminar.price) : null
  }));

  // Combine all services
  const allServices = [...transformedServices, ...validCustomServices];
  
  // Debug: Log custom services being submitted
  const customServicesToSubmit = allServices.filter(s => s.is_custom === 1);
  console.log('📤 Custom services being submitted:', customServicesToSubmit.length);
  customServicesToSubmit.forEach(s => {
    console.log(`  - ${s.name} (price: ${s.price}, is_custom: ${s.is_custom}, id: ${s.id})`);
  });

  // ============ BASE DATA ASSEMBLY ============
  
  let submissionData = {
    ...safeFormValues,
    services: allServices,
    seminars: transformedSeminars,
  };

  // ============ ENSURE DISPOSITION TYPES ARE ALWAYS PRESENT ============
  const dispositionFields = [
    'type_1_people', 'type_2_people', 'type_3_people', 'type_4_people',
    'type_5_people', 'type_6_people', 'type_7_people', 'max_capacity'
  ];
  
  dispositionFields.forEach(field => {
    if (submissionData[field] === undefined || 
        submissionData[field] === null || 
        submissionData[field] === '') {
      submissionData[field] = 0;
    }
  });

  // Ensure minimal_duration and minimal_capacity have defaults
  if (!submissionData.minimal_duration) {
    submissionData.minimal_duration = 'half_day';
  }
  
  if (submissionData.minimal_capacity === undefined || 
      submissionData.minimal_capacity === null || 
      submissionData.minimal_capacity === '') {
    submissionData.minimal_capacity = submissionData.max_capacity || 1;
  }

  // Handle max_capacity when it's 0
  if (submissionData.max_capacity === 0 || submissionData.max_capacity === '0') {
    if (submissionData.type_1_people > 0) {
      submissionData.max_capacity = submissionData.type_1_people;
    } else if (submissionData.type_5_people > 0) {
      submissionData.max_capacity = submissionData.type_5_people;
    } else {
      submissionData.max_capacity = 1;
    }
  }

  // ============ IMAGE HANDLING ============
  for (let i = 1; i <= 6; i++) {
    const imageKey = `image_${i}`;
    const descriptionKey = `image_${i}_description`;
    
    const imageFromQueue = safeImageQueueRef.current.get(imageKey);
    const imageFromForm = safeFormValues[imageKey];
    
    let hasValidImage = false;
    let imageToSubmit = null;
    
    if (imageFromQueue instanceof File) {
      hasValidImage = true;
      imageToSubmit = imageFromQueue;
    } else if (imageFromForm instanceof File) {
      hasValidImage = true;
      imageToSubmit = imageFromForm;
    } else if (typeof imageFromForm === 'string' && imageFromForm.length > 0 && imageFromForm.startsWith('http')) {
      hasValidImage = true;
      imageToSubmit = imageFromForm;
    }
    
    if (hasValidImage && imageToSubmit) {
      submissionData[imageKey] = imageToSubmit;
      if (safeFormValues[descriptionKey] && safeFormValues[descriptionKey].trim() !== '') {
        submissionData[descriptionKey] = safeFormValues[descriptionKey];
      }
    } else {
      delete submissionData[imageKey];
      delete submissionData[descriptionKey];
    }
  }

  // ============ DATA CLEANING ============
  const cleanSubmissionData = (obj) => {
    const cleaned = {};
    
    for (const [key, value] of Object.entries(obj)) {
      if (value === null || value === undefined || value === '') {
        continue;
      }
      
      if (Array.isArray(value) && value.length === 0) {
        continue;
      }
      
      if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof File) && Object.keys(value).length === 0) {
        continue;
      }
      
      cleaned[key] = value;
    }
    
    return cleaned;
  };

  return cleanSubmissionData(submissionData);
};